import {
	DemandManagementApiService,
	type MessagingClosedReason,
	type MessagingOpenStatus,
} from '@api_patient/demand_management_api.service';
import { RateApiService } from '@api_patient/rate_api.service';
import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import type { AsyncSignalValue } from '@component/async.component';
import { RouterService } from '@cortex-shim/router.service';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import type { ClientConditionApiModel, RateApiModel } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import type { State } from 'history';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useBasket } from './use-basket';
import type { CreateNewMessageSettings } from './use-create-new-message-settings';
import { useCreateNewMessageSettings } from './use-create-new-message-settings';

const logger = new Logger('useCreateNewMessage');

function arraysEqual<T>(a: T[] | null, b: T[] | null): boolean {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (a.length !== b.length) return false;
	return a.every((value, index) => value === b[index]);
}

function createNewMessageSettingsEqual(a: CreateNewMessageSettings, b: CreateNewMessageSettings): boolean {
	return (
		a.predefinedDescriptionOfMatter === b.predefinedDescriptionOfMatter &&
		arraysEqual(a.predefinedReasonForVisit, b.predefinedReasonForVisit) &&
		a.redRobinSessionId === b.redRobinSessionId &&
		a.asAgentFor === b.asAgentFor &&
		a.respondentType === b.respondentType &&
		a.representativeNin === b.representativeNin &&
		arraysEqual(a.access, b.access) &&
		a.attachFiles === b.attachFiles
	);
}

function normalizeClosedReason(closedReason: Nullable<string>): MessagingClosedReason {
	switch (closedReason) {
		case 'OutsideMonitoredHours':
		case 'CapLimitReached':
		case 'Other':
			return closedReason;
		default:
			return 'Other';
	}
}

export enum CreateNewMessageAnalyticActions {
	UsingSettings = 'Using settings',
	GoToScreen = 'Go-to-screen',
	Closed = 'Closed through demand management',
	NotAllowedToBeHere = 'Not allowed to be here',
	DoesNotUseRepresentatives = 'Does not use representatives',
	AutoSetRespondentTypeRepresentative = 'Automatically respondent type set to "representative"',
	AutoSetRespondentTypeMe = 'Automatically respondent type set to "me"',
	AutoSetReasonForVisit = 'Automatically set reason for visit',
	SelectReasonForVisit = 'Select reason for visit',
	NoReasonForVisits = 'No reason for visits',
	NoForms = 'No forms',
	NumberOfForms = 'Number of forms',
	LoadForm = 'Load form',
	FormIsInvalid = 'Form is invalid',
}

interface ScreenStartData {
	title: Nullable<string>;
	description: Nullable<string>;
}

export interface CreateNewMessageFlowData {
	price: Nullable<string>;
	requirePayment: boolean;
	reasonForVisits: ClientConditionApiModel[];
	allowRepresentative: boolean;
	screenStart: ScreenStartData;
	closedReason: MessagingClosedReason | null;
}

export type CreateNewMessageFlowScreen =
	| 'who-is-respondent'
	| 'information-gathering'
	| 'nhs-login-required'
	| 'reason-for-visit'
	| 'forms'
	| 'access'
	| 'conclusion'
	| 'error'
	| 'start'
	| 'done'
	| 'closed';

export type FlowDataValue = AsyncSignalValue<CreateNewMessageFlowData>;
export type FlowSettingsValue = AsyncSignalValue<CreateNewMessageSettings>;

interface GoToScreenOptions {
	replace?: boolean;
	formId?: number;
	formPage?: number;
	conversationId?: number;
	closedReason?: MessagingClosedReason;
	state?: State;
}

export interface CreateNewMessageContextValue {
	flowData: FlowDataValue;
	flowSettings: FlowSettingsValue;
	filteredReasonForVisits: ClientConditionApiModel[];
	isClosed: boolean;

	setupNewBasket(): AsyncResult<void>;
	loadSettings(): Promise<void>;
	deleteBasket(): Promise<void>;

	goToScreen(screen: CreateNewMessageFlowScreen, options?: GoToScreenOptions): void;
	goToReceptionHome(): void;

	updateFlowData(data: Partial<CreateNewMessageFlowData>): void;
	updateFlowSettings(settings: Partial<CreateNewMessageSettings>): void;
}

const CreateNewMessageContext = createContext<CreateNewMessageContextValue | null>(null);

export function useCreateNewMessage(): CreateNewMessageContextValue {
	const ctx = useContext(CreateNewMessageContext);
	if (ctx == null) {
		throw new Error('useCreateNewMessage must be used within a <CreateNewMessageProvider>');
	}
	return ctx;
}

export function CreateNewMessageProvider({ children }: PropsWithChildren) {
	const routerService = useResolve(RouterService);
	const universalConfig = useResolve(UniversalConfig);
	const unitsApiService = useResolve(UnitsApiService);
	const demandManagementApiService = useResolve(DemandManagementApiService);
	const rateApiService = useResolve(RateApiService);
	const { basket: activeBasket, basketId: activeBasketId, deleteActiveBasket, restore: restoreBasket } = useBasket();
	const unitService = useResolve(UnitService);
	const { getSettings, setSettings, clearSettings } = useCreateNewMessageSettings();
	const redRobinApiService = useResolve(RedRobinApiService);
	const analyticService = useResolve(AnalyticService);
	const unit = unitService.getUnitNodeInTree(universalConfig.selectedUnitId);
	const { receptionId, routeWithoutLicenceholder: routeBase } = unit;

	const [flowData, setFlowData] = useState<FlowDataValue>({ value: null, flowState: 'pending' });
	const [flowSettings, setFlowSettings] = useState<FlowSettingsValue>({ value: null, flowState: 'pending' });
	const isSettingUp = useRef(false);
	const isClosed = flowData.flowState === 'accessible' && flowData.value.closedReason !== null;

	// Refs to avoid stale closures and unstable dependencies
	const activeBasketRef = useRef(activeBasket);
	activeBasketRef.current = activeBasket;
	const activeBasketIdRef = useRef(activeBasketId);
	activeBasketIdRef.current = activeBasketId;

	const filteredReasonForVisits = ((): ClientConditionApiModel[] => {
		if (flowData.flowState !== 'accessible' || flowSettings.flowState !== 'accessible') {
			return [];
		}

		if (flowSettings.value.predefinedReasonForVisit) {
			return flowData.value.reasonForVisits.filter((rfv) => flowSettings.value.predefinedReasonForVisit?.includes(rfv.Id)) ?? [];
		}

		return flowData.value.reasonForVisits ?? [];
	})();

	const updateFlowData = useCallback((data: Partial<CreateNewMessageFlowData>): void => {
		setFlowData((prev) => {
			if (prev.value === null) return prev;
			return {
				value: { ...prev.value, ...data },
				flowState: 'accessible',
			};
		});
	}, []);

	const updateFlowSettings = useCallback(
		(settings: Partial<CreateNewMessageSettings>): void => {
			setSettings(settings);
			const nextSettings = getSettings();
			setFlowSettings((prev) => {
				if (prev.flowState === 'accessible' && createNewMessageSettingsEqual(prev.value, nextSettings)) {
					return prev;
				}

				return {
					value: nextSettings,
					flowState: 'accessible',
				};
			});
		},
		[setSettings, getSettings],
	);

	const goToScreen = useCallback(
		(screen: CreateNewMessageFlowScreen, options: GoToScreenOptions = {}): void => {
			let path = '';
			let analyticActionName: string = screen;

			switch (screen) {
				case 'start':
					path = 'start';
					break;
				case 'who-is-respondent':
					path = 'who-is-respondent';
					break;
				case 'information-gathering':
					path = 'information-gathering';
					break;
				case 'nhs-login-required':
					path = 'nhs-login-required';
					break;
				case 'reason-for-visit':
					path = 'reason-for-visit';
					break;
				case 'forms':
					path = 'forms';
					if (options.formId != null) {
						path += `/${options.formId}`;
						analyticActionName += ` - formId: ${options.formId}`;
						if (options.formPage != null) {
							path += `/${options.formPage}`;
							analyticActionName += ` page: ${options.formPage}`;
						}
					}
					break;
				case 'access':
					path = 'access';
					break;
				case 'conclusion':
					path = 'conclusion';
					break;
				case 'done':
					path = 'done';
					if (options.conversationId) {
						path += `/${options.conversationId}`;
					}
					break;
				case 'closed':
					path = 'closed';
					updateFlowData({
						closedReason: normalizeClosedReason(options.closedReason ?? null),
					});
					break;
				case 'error':
					path = 'error';
					break;
			}

			analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.GoToScreen, analyticActionName);

			const url = [routeBase, 'message', path].filter(Boolean);
			routerService.navigateByUrl(`/${url.join('/')}`, {
				replace: options.replace,
				state: options.state ?? routerService.history.location.state,
			});
		},
		[routeBase, routerService, analyticService, updateFlowData],
	);

	const goToReceptionHome = useCallback((): void => {
		// Client-side routerService.navigateByUrl leaves this flow's route tree stuck rendered
		// after leaving a flow route (e.g. this create-new-message flow); a real URL change reliably resets it.
		// The router's basename (licence holder alias) isn't applied to window.location, so it's prepended here.
		window.location.href = getAbsoluteRoute([universalConfig.alias, routeBase].filter(Boolean).join('/'));
	}, [universalConfig.alias, routeBase]);

	const deleteBasket = useCallback(async () => {
		try {
			await deleteActiveBasket(receptionId ?? -1);
			clearSettings();
			setFlowSettings({ value: null, flowState: 'pending' });
		} catch (err) {
			if (err instanceof Error) {
				logger.logError('User tried to delete basket: {message}', err.message);
			}
		}
	}, [deleteActiveBasket, receptionId, clearSettings]);

	const setupNewBasket = useCallback(async (): AsyncResult<void> => {
		logger.logInformation('Setup basket');

		isSettingUp.current = true;
		try {
			await deleteBasket();
			const basketId = await restoreBasket(receptionId ?? -1);

			if (!basketId) {
				throw new Error('No basket found...');
			}

			const queryParams = new URLSearchParams(window.location.search);

			const redRobinSessionId = queryParams.get('redRobinSessionId');
			let asAgentFor: Nullable<string> = null;

			if (redRobinSessionId) {
				const redRobinResult = await redRobinApiService.getRepresentativeNin(redRobinSessionId);

				if (redRobinResult.err) {
					logger.logError('Failed to get RedRobin session');
					return Err(redRobinResult.err);
				}

				asAgentFor = redRobinResult.ok.data.RepresentativeNin ?? null;
			} else {
				asAgentFor = queryParams.get('nin') ?? null;
			}

			updateFlowSettings({
				redRobinSessionId,
				asAgentFor,
				predefinedDescriptionOfMatter: queryParams.get('dom') ?? null,
				predefinedReasonForVisit:
					queryParams
						.get('rfv')
						?.split(',')
						.map((id) => parseInt(id, 10)) ?? null,
				attachFiles: queryParams.get('file') !== 'false',
				access: [],
				respondentType: null,
				representativeNin: null,
			});

			return Ok();
		} finally {
			isSettingUp.current = false;
		}
	}, [deleteBasket, restoreBasket, receptionId, redRobinApiService, updateFlowSettings]);

	const loadSettings = useCallback(async (): Promise<void> => {
		logger.logInformation('Load settings');

		if (isSettingUp.current) {
			return;
		}

		if (isClosed) {
			return;
		}

		let basketId = activeBasketIdRef.current;

		if (!activeBasketRef.current) {
			basketId = await restoreBasket(receptionId ?? -1);
		}

		if (!basketId) {
			throw new Error('WE NEED A BASKET!!');
		}

		const settings = getSettings();

		setFlowSettings((prev) => {
			if (prev.flowState === 'accessible' && createNewMessageSettingsEqual(prev.value, settings)) {
				return prev;
			}

			return {
				value: settings,
				flowState: 'accessible',
			};
		});

		const analyticsSettings: string[] = [];

		if (settings.asAgentFor) analyticsSettings.push('asAgentFor');
		if (settings.redRobinSessionId) analyticsSettings.push('redRobinSessionId');
		if (settings.predefinedDescriptionOfMatter) analyticsSettings.push('predefinedDescriptionOfMatter');
		if (settings.predefinedReasonForVisit) analyticsSettings.push('predefinedReasonForVisit');
		if (!settings.attachFiles) analyticsSettings.push('hideAttachFiles');

		if (analyticsSettings.length) {
			analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.UsingSettings,
				analyticsSettings.join(', '),
			);
		}
	}, [isClosed, restoreBasket, receptionId, getSettings, analyticService]);

	const hasInitializedFlowData = useRef(false);

	useEffect(() => {
		if (hasInitializedFlowData.current) return;
		hasInitializedFlowData.current = true;

		let cancelled = false;

		const resolveMessagingAccess = (status: MessagingOpenStatus): 'open' | 'closed' | 'error' => {
			switch (status.toLowerCase()) {
				case 'open':
				case 'inactive':
					return 'open';
				case 'closed':
					return 'closed';
				default:
					return 'error';
			}
		};

		const fetchPrice = async (): AsyncResult<RateApiModel> => {
			const response = await rateApiService.getMessagingConversationPrice(receptionId ?? -1);
			if (response.err) return Err(response.err);
			return Ok(response.ok.data);
		};

		const fetchTexts = async (): AsyncResult<ScreenStartData> => {
			logger.logInformation('Get start screen texts');
			const messageResult = await unitsApiService.getTexts(universalConfig.selectedUnitId, [
				'Messaging_Intro_Title',
				'Messaging_Intro_Message',
			]);
			if (messageResult.err) {
				logger.logError(
					'User tried to get start tests: {message} {statusCode}',
					messageResult.err.message,
					messageResult.err.statusCode,
				);
				return Err(messageResult.err);
			}

			return Ok({
				title: messageResult.ok.data.Messaging_Intro_Title,
				description: messageResult.ok.data.Messaging_Intro_Message,
			});
		};

		const fetchOfferData = async (): AsyncResult<{ reasonForVisits: ClientConditionApiModel[]; allowRepresentative: boolean }> => {
			logger.logInformation('Get offer data');

			const unitConfigResult = await unitsApiService.getUnitConfig(universalConfig.selectedUnitId);
			if (unitConfigResult.err) return Err(unitConfigResult.err);

			const allowRepresentative = unitConfigResult.ok.data.Offer?.AllowClientAgents || false;

			if (unitConfigResult.ok.data.Offer?.HasConditionWorkflow === false) {
				return Ok({ reasonForVisits: [], allowRepresentative });
			}

			const reasonForVisitsResult = await unitsApiService.getConditions(universalConfig.selectedUnitId);
			if (reasonForVisitsResult.err) {
				logger.logError(
					'User tried to get reason for visits: {message} {statusCode}',
					reasonForVisitsResult.err.message,
					reasonForVisitsResult.err.statusCode,
				);
				return Err(reasonForVisitsResult.err);
			}

			return Ok({ reasonForVisits: reasonForVisitsResult.ok.data, allowRepresentative });
		};

		(async () => {
			setFlowData({ value: null, flowState: 'pending' });

			const accessResult = await demandManagementApiService.getMessagingOpenStatus(receptionId);
			if (cancelled) return;
			if (accessResult.err) {
				setFlowData({ value: null, flowState: 'error' });
				return;
			}

			const messagingAccess = resolveMessagingAccess(accessResult.ok.data.status);
			if (messagingAccess === 'closed') {
				const closedReason = normalizeClosedReason(accessResult.ok.data.closedReason);

				setFlowData({
					value: {
						price: null,
						requirePayment: false,
						screenStart: { title: null, description: null },
						reasonForVisits: [],
						allowRepresentative: false,
						closedReason,
					},
					flowState: 'accessible',
				});

				analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.Closed, 'Initial load');
				goToScreen('closed', { replace: true, closedReason });
				return;
			}

			if (messagingAccess === 'error') {
				setFlowData({ value: null, flowState: 'error' });
				return;
			}

			const [priceResult, startMessageResult, offerDataResult] = await Promise.all([fetchPrice(), fetchTexts(), fetchOfferData()]);

			if (cancelled) return;

			if (priceResult.err || startMessageResult.err || offerDataResult.err) {
				setFlowData({ value: null, flowState: 'error' });
				return;
			}

			setFlowData({
				value: {
					price: priceResult.ok.PaymentWorkflow !== 'NoPayment' ? priceResult.ok.Price.Formatted : null,
					requirePayment: priceResult.ok.PaymentWorkflow === 'ByUserBeforeEntering',
					screenStart: startMessageResult.ok,
					reasonForVisits: offerDataResult.ok.reasonForVisits,
					allowRepresentative: offerDataResult.ok.allowRepresentative,
					closedReason: null,
				},
				flowState: 'accessible',
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [receptionId, demandManagementApiService, rateApiService, unitsApiService, universalConfig, analyticService, goToScreen]);

	const value: CreateNewMessageContextValue = {
		flowData,
		flowSettings,
		filteredReasonForVisits,
		isClosed,
		setupNewBasket,
		loadSettings,
		deleteBasket,
		goToScreen,
		goToReceptionHome,
		updateFlowData,
		updateFlowSettings,
	};

	return <CreateNewMessageContext.Provider value={value}>{children}</CreateNewMessageContext.Provider>;
}
